import { addDays, addMinutes, formatRFC3339, endOfDay, startOfDay } from 'date-fns'
import dateFormat from 'dateformat'
import { makeStyles } from '@material-ui/core/styles'

const dateHelper = {
  /**
   * Convert date string to date object
   * @param {string} dateStr the date with year, month, and day **ONLY** i.e. '2021-08-31'
   */
  stringToDate: dateStr => {
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10))
    const returnDate = new Date()
    returnDate.setFullYear(year, month - 1, day)
    return returnDate
  },
  /**
   * Convert date to date string
   * @param {Date} dateObj the date to transform
   * @param {string} [format] the format (or mask) for the return string
   * @param {boolean} [format] convert local to UTC
   */
  dateToString: (dateObj, format = 'yyyy-mm-dd', utc = false) => dateFormat(dateObj, format, utc)
}

function getDaysDict (slots) {
  const dict = {}
  slots.forEach(s => {
    const start = new Date(s.start)
    const day = start.getFullYear() + '/' + (start.getMonth() + 1) + '/' + start.getDate()
    if (dict[day]) {
      dict[day].push(start)
    } else {
      dict[day] = [start]
    }
  })

  return dict
}

/**
 * Format start and end dates for use in cronofy api request
 * @param {{ start: Date; end: Date; }} param0
 */
function formatAvailablePeriods ({ start, end }) {
  return { start: formatRFC3339(start), end: formatRFC3339(end) }
}

/**
 * @param {any[]} ogOutOfOfficeInstances
 * @param {number} [leadTime]
 * @returns {{ start: Date; end: Date; }[]}
 */
function getAvailablePeriods (ogOutOfOfficeInstances, leadTime = 0) {
  /** The maximum days away the end Date can be **Must Be less Than 35** */
  const maxDaysOut = 14
  const earliest = addMinutes(new Date(), leadTime)
  const latest = addDays(new Date(), maxDaysOut)

  if (!ogOutOfOfficeInstances.length) {
    return [{ start: earliest, end: latest }]
  }
  /** @type {{ start: Date; end: Date }[]} */
  const availablePeriods = []
  const outOfOfficeInstances = ogOutOfOfficeInstances.map(({ start_date, end_date }) => ({
    start: startOfDay(dateHelper.stringToDate(start_date)),
    end: endOfDay(dateHelper.stringToDate(end_date))
  })).filter(({ start }) => start < latest)

  if (!outOfOfficeInstances.length) {
    return [{ start: earliest, end: latest }]
  }

  if (outOfOfficeInstances[0].start < earliest) {
    availablePeriods.push({ start: outOfOfficeInstances[0].end, end: new Date(latest) })
    outOfOfficeInstances.splice(0, 1)
  } else {
    availablePeriods.push({ start: new Date(earliest), end: new Date(latest) })
  }

  for (const instance of outOfOfficeInstances) {
    const lastIndex = availablePeriods.length - 1
    if (instance.end < availablePeriods[lastIndex].start) {
      continue
    }
    if (instance.start < availablePeriods[lastIndex].start) {
      availablePeriods[lastIndex].start = instance.end
      continue
    }
    availablePeriods[lastIndex].end = instance.start
    availablePeriods.push({ start: instance.end, end: new Date(latest) })
  }

  return availablePeriods
}

function getSlots ({ element_token, member_sub, drop }) {
  const url = 'https://api.cronofy.com/v1/availability?et=' + element_token

  const outOfOfficeInstances = drop.data.agent_out_of_office_instances.map(({ attributes }) => attributes)

  const available_periods = getAvailablePeriods(outOfOfficeInstances, drop.data.lead_time).map(period => formatAvailablePeriods(period))

  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    'Cronofy-Element': 'v1.9.1, SlotPicker'
  }
  const body = {
    participants: [
      {
        members: [
          {
            sub: member_sub,
            managed_availability: true
          }
        ],
        required: 'all'
      }
    ],
    required_duration: { minutes: drop.data.duration },
    buffer: {
      before: { minutes: drop.data.buffer_time },
      after: { minutes: drop.data.buffer_time }
    },
    response_format: 'overlapping_slots',
    available_periods
  }
  return fetch(
    url,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: headers
    }
  ).then(response => response.json())
}

const calendarStyles = makeStyles(theme => ({
  showMoreButton: {
    backgroundColor: '#FFF',
    border: '1px solid black',
    height: 40
  },
  showMoreDiv: {
    marginTop: 10
  },
  title: {
    display: 'flex',
    alignItems: 'center'
  },
  calendarDropOuter: {
    width: '90%',
    height: 360,
    margin: '25px auto 10px auto',
    boxShadow: '0px 0px 7px 0px rgba(0,0,0,0.68)',
    borderRadius: 12
  },
  calendarDropHeader: {
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    color: theme.palette.primary.contrastText,
    height: 45,
    alignItems: 'center',
    padding: '0px 10px'
  },
  calendarDropBody: {
    height: 310,
    overflowY: 'auto'
  },
  calendarDropLoading: {
    height: 310,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  calendarDropCompleteCard: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: 10,
    textAlign: 'center',
    padding: '15px 10px'
  },
  agentNameDiv: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 5,
    color: 'rgba(0,0,0,0.7)'
  },
  agentAvatarDiv: {
    display: 'flex',
    justifyContent: 'center'
  },
  slotsDiv: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 10,
    margin: '10px 0px'
  },
  optionsText: {
    textAlign: 'center',
    font: 'normal normal 600 10px/14px Open Sans',
    letterSpacing: 0,
    color: '#2C2C2C',
    opacity: 1,
    marginTop: 5,
    marginBottom: 10,
    cursor: 'pointer'
  },
  dateAndTimeSlotsDiv: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: 5,
    margin: '10px 0px'
  },
  calendarPage: {
    padding: '0px 10px'
  },
  meetingTime: {
    border: '1px solid rgba(0,0,0,0.8)',
    padding: 8,
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 5,
    color: 'rgba(0,0,0,0.7)',
    marginBottom: 15,
    textAlign: 'center'
  },
  emailConfirmDiv: {
    border: '1px solid rgba(0,0,0,0.8)',
    padding: 8,
    borderRadius: 5,
    color: 'rgba(0,0,0,0.7)',
    marginBottom: 10,
    textAlign: 'center'
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  timeSelctionError: {
    color: 'red',
    whiteSpace: 'nowrap'
  },
  calendarDropComplete: {
    backgroundColor: 'white',
    height: 255,
    margin: '15px 10px'
  },
  calendarDropCompleteAvatar: {
    width: 70,
    height: 70,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  calendarDropCompleteDate: {
    color: 'rgba(0,0,0,0.6)',
    fontSize: '1.2em',
    marginTop: 5,
    marginBottom: 8
  },
  textReminderDate: {
    color: 'rgba(0,0,0,0.8)',
    fontSize: '0.8em',
    marginTop: 4,
    marginBottom: 2
  },
  textReminderEmailMessage: {
    marginTop: 7,
    color: 'rgba(0,0,0,0.6)',
    fontSize: '0.75em',
    whiteSpace: 'nowrap',
    marginLeft: -10
  },
  textReminderPrompt: {
    border: '1px solid rgba(0,0,0,0.8)',
    padding: 10,
    borderRadius: 10,
    textAlign: 'center',
    fontWeight: 600,
    lineHeight: '25px'
  },
  textReminderButtonDiv: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 10,
    marginTop: 10
  },
  reminderMenuItem: {
    padding: '2px 15px',
    fontSize: '14px',
    minHeight: 36
  }
  // textReminderButton: {
  //   border: '2px solid rgba(0,0,0,0.6)',
  //   width: '100%',
  //   textAlign: 'center',
  //   padding: '10px 0px',
  //   fontWeight: 600
  // }
}))

export { calendarStyles, getSlots, getDaysDict }
